/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Pure SHA-256 fingerprint of an ML-DSA-65 signing public key, plus the
 * F16 human-readable formatter (grouped 5-char base32). The pinned form
 * lives inside the F11 envelope's `VaultContents.contactKeys[i].fingerprint`
 * as base64nopad bytes; the formatted form is what users see in the UI.
 *
 * The fingerprint is over the *signing* public key, not the encryption
 * public key — see `docs/Application_redesign.md` §3 stage 4: encryption
 * keys rotate freely under a stable signing-key pin.
 *
 * Format: SHA-256(publicKey) → 32 bytes → base32 RFC 4648 nopad (52 chars)
 *         → take first 35 chars → group into 5-char chunks separated by spaces.
 *
 * Example: "R5JKL TQXNV WMPDG 23YBA 7CSFE QHRT4 6UNXY"
 *
 * 175 bits of preimage strength — far above the ~80-bit floor for
 * fingerprint comparison attacks. Truncation makes voice/print
 * comparison practical for humans.
 */

import { sha256 } from '@noble/hashes/sha2.js';
import { base32nopad } from '@scure/base';

export const FINGERPRINT_LENGTH = 32;

/** Number of 5-character groups in the formatted fingerprint. */
export const FINGERPRINT_GROUPS = 7;

/** Total significant base32 chars (groups × 5). */
export const FINGERPRINT_CHARS = FINGERPRINT_GROUPS * 5;

/** Group separator. */
const GROUP_SEPARATOR = ' ';

/** Truncation marker for compact form (Unicode horizontal ellipsis). */
const TRUNCATION_MARK = ' …';

/** Number of leading groups kept by `truncatedFingerprint`. */
const TRUNCATED_GROUPS = 2;

/**
 * Compute the 32-byte SHA-256 fingerprint of a signing public key.
 *
 * Determinism: the digest is a pure function of the input bytes; the
 * same key always yields the same fingerprint.
 */
export const fingerprint = (signingKey: Uint8Array): Uint8Array =>
  sha256(signingKey);

/**
 * Constant-time-ish equality check on two fingerprints. Both inputs
 * must be 32 bytes; mismatched lengths return `false` immediately.
 *
 * Note: this isn't a security-critical comparison (the fingerprints are
 * public values), but using a length-equal byte loop avoids `Buffer`
 * tooling differences between browsers and Node.
 */
export const fingerprintsEqual = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= (a[i] as number) ^ (b[i] as number);
  }
  return diff === 0;
};

/**
 * Insert a space every 5 characters of `chars`. Pure helper, exposed
 * for tests; production callers use `formatFingerprint` /
 * `truncatedFingerprint` instead.
 */
export const groupBase32 = (chars: string): string => {
  const groups: string[] = [];
  for (let i = 0; i < chars.length; i += 5) {
    groups.push(chars.slice(i, i + 5));
  }
  return groups.join(GROUP_SEPARATOR);
};

/**
 * Format a public key as a human-readable fingerprint:
 *
 *   SHA-256(publicKey) → base32-nopad → first 35 chars → 7 × 5-char groups.
 *
 * Result is 41 characters: 35 base32 chars + 6 separators. Stable across
 * runs; identical input always yields identical output.
 *
 * @example
 *   formatFingerprint(somePublicKey)
 *   // → "R5JKL TQXNV WMPDG 23YBA 7CSFE QHRT4 6UNXY"
 */
export const formatFingerprint = (publicKey: Uint8Array): string => {
  const digest = fingerprint(publicKey);
  const encoded = base32nopad.encode(digest); // 52 chars for 32-byte input
  const truncated = encoded.slice(0, FINGERPRINT_CHARS);
  return groupBase32(truncated);
};

/**
 * Compact form for tight UIs (e.g. recipient table rows). Returns the
 * first two groups of `formatFingerprint`, followed by an ellipsis:
 *
 *   "R5JKL TQXNV …"
 *
 * The full form is one click/hover away — never let truncation be
 * the only thing the user can see for a key they need to verify.
 */
export const truncatedFingerprint = (publicKey: Uint8Array): string => {
  const full = formatFingerprint(publicKey);
  // Slice off the first N groups + (N-1) separators between them.
  const cutoff = TRUNCATED_GROUPS * 5 + (TRUNCATED_GROUPS - 1);
  return full.slice(0, cutoff) + TRUNCATION_MARK;
};
